/**
 * The booking action returns zod's `flatten().fieldErrors`: one array of messages per
 * top-level key, plus `general` for anything that is not a field. These helpers decide
 * where each message is shown, so a failure can never be invisible.
 */
export type FormErrors = Record<string, string | readonly string[] | undefined>;

/**
 * Keys shown under their own input, per form mode. Each one must have a matching
 * <FieldError> in QuickLeadsForm: a key listed here but not rendered there is hidden
 * from both the field and the catch-all, which is the bug this module exists to stop.
 */
export const INLINE_KEYS = {
  student: ['name', 'instrument', 'ageGroup', 'email', 'phone', 'leadSource'],
  family: ['parentName', 'parentInstrument', 'students', 'email', 'phone', 'leadSource'],
} as const;

export type FormMode = keyof typeof INLINE_KEYS;

export function errorFor(errors: FormErrors | undefined, key: string): string | undefined {
  const value = errors?.[key];
  if (value === undefined) return undefined;
  const message = typeof value === 'string' ? value : value[0];
  return message || undefined;
}

/**
 * Messages the current mode has no input slot for. Before this existed a server error
 * on any key but `email` and `general` rendered nothing: the parent pressed submit and
 * the form simply did not respond.
 */
export function unplacedErrors(
  errors: FormErrors | undefined,
  mode: FormMode,
): { key: string; message: string }[] {
  const inline: readonly string[] = INLINE_KEYS[mode];
  return failedFields(errors)
    .filter((key) => key !== 'general' && !inline.includes(key))
    .map((key) => ({ key, message: errorFor(errors, key) as string }));
}

/** Field names only, never values: this is what goes to analytics. */
export function failedFields(errors: FormErrors | undefined): string[] {
  return Object.keys(errors ?? {})
    .filter((key) => errorFor(errors, key) !== undefined)
    .sort();
}

/**
 * Names of the inputs the browser's own validation is rejecting, in form order, so the
 * first entry is the field the browser points the person at. Radio groups share a name
 * and are reported once. Names only, never values.
 */
export function invalidFieldNames(form: HTMLFormElement): string[] {
  const names = Array.from(form.elements)
    .filter(
      (el): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
        'willValidate' in el && (el as HTMLInputElement).willValidate && !(el as HTMLInputElement).checkValidity(),
    )
    .map((el) => el.name || el.id)
    .filter(Boolean);
  return [...new Set(names)];
}
